import './components/vue_components/progress_bar.js';
import Graphy from './graphy.js';
import Graph from './interface.js';
import Remote from './remote.js';
import ContentContext from './content_context';
import ContentManager from './content_manager.js';
import key from 'keymaster';
import Comments from '../comments/comments.js';
import routie from '../../libs/vendor/routie/routie.js';
import _ from 'underscore';
import Panel from './panels.js';
import Utils from './utils.js';
import LoopsLockManager from './lock_manager.js';
import PurpleGuideRenderer, {
  SessionCompletedPopupRenderer,
  SessionLeavePopupRenderer,
  syncConfirmPopup,
} from './reactLoader.js';

export default class Viewer extends Graphy {
  get contentManager() {
    return this._contentManager;
  }

  set contentManager(value) {
    this._contentManager = value;
  }

  get inFullscreen() {
    return this._inFullscreen;
  }

  set inFullscreen(value) {
    this._inFullscreen = value;
  }

  get previousNode() {
    return this._previous;
  }

  get currentNode() {
    return this._current;
  }

  get currentParent() {
    return this._current.neighborhood('node[!group]');
  }

  get previousParent() {
    return this._previous.neighborhood('node[!group]');
  }

  get currentHub() {
    return this._currentHub;
  }

  set currentHub(value) {
    if (!value || !value.length) {
      return;
    }

    if (this.currentHub) {
      this.currentHub.removeClass('current-hub');
    }

    value.addClass('current-hub');

    this._currentHub = value;
  }

  set currentNode(value) {
    if (!value || !value.length) {
      return;
    }

    if (!this._previous) {
      this._previous = value;
    }

    if (this.lockManager.isLocked(value)) {
      return;
    }

    if (this._current) {
      this._previous = this._current;
      this._previous.removeClass('selected');
    }

    this._current = value;
    this._current.addClass('selected');
    this.open(this._current);

    if (this.isHub(this._current)) {
      this.currentHub = this._current;
    } else if (this.isHub(this.currentParent)) {
      this.currentHub = this.currentParent;
    }

    if (location.pathname.startsWith('/learn') && !this.isTeacher()) {
      if (this.isHub(this._current)) {
        PurpleGuideRenderer.render('theFirstHub', null, this.getPurpleGuideNode());
      } else {
        PurpleGuideRenderer.render('theFirstNode', null, this.getPurpleGuideNode());
      }
    }
  }

  getPurpleGuideNode() {
    return $('#purpleGuideViewerWrapper')[0] || $('<div id="purpleGuideViewerWrapper"></div>').appendTo('body')[0];
  }

  getData(node, attribute) {
    if (attribute) {
      return typeof node.data === 'function' ? node[attribute]() : node[attribute];
    }
    return typeof node.data === 'function' ? node.data() : node.data;
  }

  getCheckmarkDataForNode(node) {
    const nodeData = this.getData(node);
    const position = this.getData(node, 'position');
    nodeData.checkmarkId = `${nodeData.id}_checkmark`;
    return {
      group: 'nodes',
      type: 'state',
      classes: 'accepted',
      position: {
        x: position.x + 13,
        y: position.y + 13,
      },
      data: {
        id: nodeData.checkmarkId,
        parentId: nodeData.id,
        initialized: true,
        group: 'indicators',
        type: 'state',
        state: 'accepted',
      },
    };
  }

  addCheckmarkById(uuid) {
    if (this.graph.cy.$(`#${uuid}_checkmark`).length === 0) {
      const node = this.graph.cy.$(`#${uuid}`);
      node.addClass('checkmark');
      const checkmarkNode = this.getCheckmarkDataForNode(node);
      this.graph.cy.add(checkmarkNode);
      this.disableIndicators();
    }
  }

  removeCheckmarkById(uuid, state) {
    const node = this.graph.cy.$(`#${uuid}`);
    this.graph.cy.$(`#${node.data().checkmarkId}`).remove();
    if (state === 'active') {
      node.removeClass('checkmark');
    }
  }

  getNotificationDataForNode(node) {
    const nodeData = this.getData(node);
    const position = this.getData(node, 'position');

    const nodes = [];
    const existingNotificationElement = this.graph.cy
      ? this.graph.cy.$(`#${nodeData.id}_notification`).length > 0
      : false;
    const existingAnnounceElement = this.graph.cy ? this.graph.cy.$(`#${nodeData.id}_announce`).length > 0 : false;
    if (node.hasAnyUnreadNotifications && !existingNotificationElement && !existingAnnounceElement) {
      nodes.push({
        group: 'nodes',
        type: 'notification',
        classes: 'notification',
        position: {
          x: position.x + 13,
          y: position.y - 13,
        },
        data: {
          id: `${nodeData.id}_notification`,
          parentId: nodeData.id,
          initialized: true,
          group: 'indicators',
          type: 'notification',
        },
      });
    }

    if (node.hasAnnounceNotification && !existingAnnounceElement) {
      let elementType;
      let elementPosition = {};

      if (nodeData.parentId === null) {
        elementType = 'announceHub';
        elementPosition = {
          x: position.x + 41,
          y: position.y - 41,
        };
      } else {
        elementType = 'announceNode';
        elementPosition = {
          x: position.x + 13,
          y: position.y - 13,
        };
      }

      nodes.push({
        group: 'nodes',
        type: elementType,
        classes: 'notification',
        position: elementPosition,
        data: {
          id: `${nodeData.id}_announce`,
          parentId: nodeData.id,
          initialized: true,
          group: 'indicators',
          type: elementType,
        },
      });
    }
    return nodes;
  }

  addNotificationById(uuid, type = 'notification') {
    const node = this.graph.cy.$(`#${uuid}`);
    if (node && node.length) {
      node.hasAnyUnreadNotifications = type === 'notification';
      node.hasAnnounceNotification = type === 'announce';

      const notificationNodes = this.getNotificationDataForNode(node);
      notificationNodes.forEach((notificationNode) => {
        this.graph.cy.add(notificationNode);
      });
      this.disableIndicators();
    }
  }

  removeNotificationById(uuid) {
    const notificationNode = this.graph.cy.$(`#${uuid}_notification`);
    if (notificationNode.length) {
      notificationNode.remove();
    }
  }

  markAnnouncementAsRead(node) {
    const nodeId = node.id();
    const announceNode = this.graph.cy.$(`#${nodeId}_announce`);
    if (announceNode.length) {
      this.remote.put(`notifications/read/${nodeId}/${this.userUuid}`);
      announceNode.remove();
    }
  }

  constructor(options) {
    super(options);

    $('body').attr('data-user-is-teacher', this.options.teacherInSession);

    this.graph = new Graph(document.getElementById('viewer'), {
      ungrabbable: true,
      userZoomingEnabled: true,
      userPanningEnabled: true,
      zoom: 0.6,
      checkHubCompletion: this.checkHubCompletion.bind(this),
    });

    this.mask_values = [1, 2, 4, 8, 16, 32, 64, 128];

    this.lockManager = new LoopsLockManager(options.role);

    this.remote = new Remote('', {});

    key.filter = function (event) {
      var el = event.target || event.srcElement;
      return !(el.isContentEditable || el.tagName == 'INPUT' || el.tagName == 'SELECT' || el.tagName == 'TEXTAREA');
    };

    window.addEventListener('beforeunload', (e) => {
      if (this.contentManager && this.contentManager.dirty) {
        var confirmationMessage = ' ';
        e.returnValue = confirmationMessage; // Gecko, Trident, Chrome 34+
        return confirmationMessage; // Gecko, WebKit, Chrome <34
      }
    });

    if (this.supportsCommunication()) {
      this.comments = new Comments(options);
      this.comments.init();
    }

    this.goNext = _.throttle(function (navigation) {
      this.verifyDirtyEditContext()
        .then(() => {
          if (
            typeof navigation != 'undefined' &&
            typeof navigation.next != 'undefined' &&
            typeof navigation.next.uuid != 'undefined'
          ) {
            this.currentNode = this.graph.cy.$('#' + navigation.next.uuid);
            if (this.inSession()) {
              history.pushState({ id: navigation.next.uuid }, '', '#' + navigation.next.uuid);
            }
          }
        })
        .catch((err) => {
          console.log(err);
          Utils.noop();
        });
    }, 800);

    this.goPrev = _.throttle(function (navigation) {
      this.verifyDirtyEditContext()
        .then(() => {
          if (
            typeof navigation != 'undefined' &&
            typeof navigation.parent != 'undefined' &&
            typeof navigation.parent.uuid != 'undefined'
          ) {
            this.currentNode = this.graph.cy.$('#' + navigation.parent.uuid);
            if (this.inSession()) {
              history.pushState({ id: navigation.parent.uuid }, '', '#' + navigation.parent.uuid);
            }
          }
        })
        .catch((err) => {
          console.log(err);
          Utils.noop();
        });
    }, 800);
  }

  fetchGraphData() {
    return this.remote.get(this.getGraphURL());
  }

  isTeacher() {
    return document.body.getAttribute('data-user-is-teacher') === 'true';
  }

  isTeacherInSession() {
    return this.options.teacherInSession;
  }

  disableIndicators() {
    this.graph.cy.$('node').forEach((node) => {
      if (node.data().group === 'indicators') {
        node.style('events', 'no');
      }
    });
  }

  build() {
    this.fetchGraphData().then((data) => {
      if (data.length === 0) {
        return;
      }

      if (!this.isTeacherInSession()) {
        data.forEach((item) => {
          if (item.data['checkmark_state'] === 'accepted') {
            data.push(this.getCheckmarkDataForNode(item));
          }
          if (item.hasAnyUnreadNotifications || item.hasAnnounceNotification) {
            const notificationNodes = this.getNotificationDataForNode(item);
            notificationNodes.forEach((notificationNode) => {
              data.push(notificationNode);
            });
          }
        });
      }

      this.graph.build(data);
      this.setNodeHeaders(this);
      this.useZoomDependentStyles();
      this.graph.cy.on('tap', 'node', (e) => {
        this.verifyDirtyEditContext()
          .then(() => {
            if (e.target.hasClass('accepted') || e.target.hasClass('notification') || e.target.hasClass('announce')) {
              this.currentNode = this.graph.cy.$(`#${e.target.data().parentId}`);
            } else {
              this.currentNode = e.target;
            }
            if (this.inSession()) {
              history.pushState({ id: e.target.id() }, '', '#' + e.target.id());
            }
          })
          .catch(Utils.noop);
      });

      this.graph.cy.on('tapdragover', '[?group]', (ev) => {
        this.mouseOver(ev.target);
      });

      this.graph.cy.on('tapdragout', '[?group]', (ev) => {
        this.mouseOut(ev.target);
      });

      this.graph.cy.on('tapdragover, tapdrag', 'node', (ev) => {
        this.mouseOver(ev.target);
      });

      this.graph.cy.on('tapdragout', 'node', (ev) => {
        ev.target.hover = false;
        this.mouseOut(ev.target);
      });

      document.addEventListener('node.unlocked', (event) => {
        const nodeId = event.detail;
        const node = this.graph.cy.$(`#${nodeId}`);
        node.data('lock_state', 'unlocked');
        this.resolveAccessModel();
        this.open(node);
      });

      $(document).on('loopNameClicked', (ev, data) => {
        if (this.isOpening) {
          return;
        }
        this.isOpening = true;
        window.location.hash = '';
        this.currentNode = null;
        let context = new ContentContext(this.options.circle_name);
        let circle = new ContentManager(context, this.remote, {
          ...this.options,
          viewer: this,
          session_id: this.options.session_id,
          getURLFor: this.getURLFor.bind(this),
          collection_id: this.options.collection_id,
        });
        circle.loadModal().then((panel) => {
          panel.show();
          panel.eventSource.on('didShow.circlePanel', () => {
            this.centerGraph();
            this.isOpening = false;
          });
        });
        $('.node-header.selected').removeClass('selected');
        this.graph.cy.$('.selected').removeClass('selected');
      });

      $('.lp--js-leaveLink').on(Utils.interactionEvent, (ev) => {
        ev.preventDefault();
        SessionLeavePopupRenderer.render(this.options.userToken, this.options.session_id);
      });

      if (typeof this.options.initial_initiated_progress != 'undefined') {
        $(document).on('progress.update', (ev, progress) => {
          document.dispatchEvent(new CustomEvent('progressUpdated', { detail: progress }));
        });
      }

      document.addEventListener('progressUpdated', (event) => {
        const progress = event.detail;
        if (progress.completed_progress === 1 || progress.done_progress === 1) {
          SessionCompletedPopupRenderer.render(progress, this.options.certification_enabled);
        }
      });

      this.graph.cy.$('node[!group]').forEach((hub) => this.checkHubCompletion(hub));

      this.disableIndicators();

      this.graph.cy.minZoom(0.3);

      this.graph.cy.$('node').removeClass('setup');
      super.build('#viewer');
      this.focusGraph().then(() => {});
    });

    if (this.shouldDisplayCircleOnLoad() && window.location.hash.length == 0) {
      let context = new ContentContext(this.options.circle_name);
      let circle = new ContentManager(context, null, {
        ...this.options,
        viewer: this,
        session_id: this.options.session_id,
        collection_id: this.options.collection_id,
      });
      circle.loadModal().then((panel) => {
        panel.show();
        panel.eventSource.on('didShow.circlePanel', () => {
          this.centerGraph();
          this.isOpening = false;
        });
      });
    }
  }

  verifyDirtyEditContext() {
    let _t = this;
    return new Promise(async function (resolve, reject) {
      if (_t.contentManager && _t.contentManager.dirty) {
        if (await syncConfirmPopup(i18n.__('viewer.confirm_unsaved_changes'))) {
          _t.contentManager.dirty = false;
          resolve();
        } else {
          reject();
        }
      } else {
        resolve();
      }
    });
  }

  inSession() {
    return true;
  }

  shouldDisplayCircleOnLoad() {
    return this.options.displayCircleOnLoad && !this.options.current_node && window.location.hash.length == 0;
  }

  focusGraph() {
    return new Promise((resolve) => {
      let staticNodeDetermination = () => {
        this.resolveAccessModel();
        if (this.options.current_node) {
          let node = this.graph.cy.$(`#${this.options.current_node}`);
          if (this.lockManager.isLocked(node)) {
            let pos = this.determinePanPosition(node);
            this.graph.cy.viewport({ zoom: 1.1, pan: pos });
          } else {
            let id = node.id();
            history.replaceState({ id }, '', `#${id}`);
          }
        } else if (this.options.start_node) {
          this.resizeGraphToViewport();
        } else {
          this.graph.cy.animate({ fit: { pading: 200 } });
        }
      };
      if (window.location.hash.length == 0) {
        staticNodeDetermination();
        resolve();
      } else {
        routie(':node/:entity/:uuid', (node, entity, uuid) => {
          this.resolveAccessModel();
          var actualNode = this.graph.cy.$('#' + node);
          if (actualNode && actualNode.length > 0 && !this.lockManager.isLocked(actualNode)) {
            this._current = actualNode;
            this._current.addClass('selected');
            this.open(this._current).then((viewer) => {
              return viewer.openComsContextFor(entity, uuid);
            });
            routie.removeAll();
          } else if (actualNode && actualNode.length > 0 && this.lockManager.isLocked(actualNode)) {
            let pos = this.determinePanPosition(actualNode);
            this.graph.cy.viewport({ zoom: 1.1, pan: pos });
          } else {
            staticNodeDetermination();
          }
          resolve();
        });
      }
      routie('last', () => {
        const lastHub = this.graph.cy.$('.last-hub');
        const id = lastHub ? lastHub.data().id : null;
        window.location.hash = id ? `#${id}` : '';
      });
      routie(':node', (node) => {
        this.resolveAccessModel();
        var actualNode = this.graph.cy.$('#' + node);
        if (actualNode && actualNode.length > 0 && !this.lockManager.isLocked(actualNode)) {
          this.currentNode = actualNode;
        } else if (actualNode && actualNode.length > 0 && this.lockManager.isLocked(actualNode)) {
          let pos = this.determinePanPosition(actualNode);
          this.graph.cy.viewport({ zoom: 1.1, pan: pos });
        } else {
          staticNodeDetermination();
        }
      });
    });
  }

  openByUUID(uuid) {
    let node = this.graph.cy.$('#' + uuid);
    this.currentNode = node;
  }

  open(node) {
    this.resetViewport();
    let _t = this;

    let navigation = this.resolveNavigationItems(node);

    let context = new ContentContext(
      node.data('name'),
      node.data('image'),
      node.id(),
      node.data('type'),
      node.data('group'),
    );

    let viewer;

    $(document).off('keydown');
    $(document).on('keydown', (ev) => {
      if (document.activeElement.nodeName === 'BODY') {
        navigation = (navigation && navigation.navigation) || navigation;
        if (ev.code === 'ArrowRight') {
          this.goNext(navigation);
        }
        if (ev.code === 'ArrowLeft') {
          this.goPrev(navigation);
        }
      }
    });

    viewer = new ContentManager(context, this.remote, {
      ...this.options,
      getURLFor: this.getURLFor.bind(this),
      navigation: navigation,
      fullscreen: this.inFullscreen,
      viewer: this,
      node: node,
      setCheckmark: (done = true) => {
        if (done) {
          let cachedNode = node;
          cachedNode.data('checkmark_state', 'done');
          cachedNode
            .addClass('checkmark')
            .style({
              'text-opacity': 1.0,
              'background-image-opacity': 0.0,
            })
            .animate({
              style: {
                'background-image-opacity': 0.99,
                'border-opacity': 0.99,
                'text-opacity': 0.99,
              },
              duration: 300,
              complete: function () {
                cachedNode.animate({
                  style: {
                    'background-image-opacity': 0.3,
                    'border-opacity': 0.3,
                    'text-opacity': 0.3,
                  },
                  duration: 300,
                  complete: function () {
                    cachedNode.removeStyle();
                  },
                });
              },
            });
        } else {
          node.removeData('checkmark_state');
          node.removeClass('checkmark');
        }
        this.checkHubCompletion(node);
      },
    });

    if (this.options.start_node != node.id()) {
      this.markProgress(node);
    }

    this.contentManager = viewer;

    let stackOnTop = true;
    let oldModal;
    if (Panel.hasPanelDisplayed()) {
      oldModal = Panel.currentPanel;
    }
    return viewer
      .loadModal()
      .then((modal) => {
        modal.hideMountPoint(function (e) {
          e.preventDefault();
          _t.verifyDirtyEditContext()
            .then(() => {
              Panel.hide(this);
            })
            .catch(Utils.noop);
        });

        if (typeof oldModal != 'undefined') {
          if (modal.type == Panel.CIRCLE) {
            stackOnTop = false;
          } else if (modal.type == Panel.HUB && (oldModal.type == Panel.HUB || oldModal.type == Panel.NODE)) {
            stackOnTop = false;
          } else if (modal.type == Panel.NODE && oldModal.type == Panel.NODE) {
            stackOnTop = this.newNodeOver();
          }
        }

        this.verifyDirtyEditContext().then(() => {
          modal.show({ stackOnTop: stackOnTop });
        });

        modal.eventSource.on('willHide.panel', () => {
          if (node == this.currentNode) {
            this.currentNode = null;
          }
        });

        modal.eventSource.on('didShow.panel', () => {
          let id = node.id();
          history.pushState({ id }, '', `#${id}`);

          this.centerGraph(true);

          $('.navigation-arrow').on('keypress', function () {
            $(this).click();
          });
        });

        this.markAnnouncementAsRead(node);

        return viewer;
      })
      .catch(function (err) {
        console.log(err);
      });
  }

  newNodeOver() {}

  getIndicatorType(node, type) {
    return this.graph.cy.filter((item) => {
      return item.data().group === 'indicators' && item.data().type === type && item.data().parentId === node.id();
    });
  }

  resolveNavigationItems(node) {
    this.checkHubCompletion(node);
    let t = this;
    let items = null;
    const orderedHubIds = window.viewer.options.orderedHubIds || [];
    const lastHubId = orderedHubIds[orderedHubIds.length - 1];
    const firstHubId = orderedHubIds[0];

    if (node.is('[!group]')) {
      items = node.neighbourhood('[?group]');

      items = items.sort(function (a, b) {
        return t.slotSortOrder.indexOf(a.data('slot')) > t.slotSortOrder.indexOf(b.data('slot')) ? 1 : -1;
      });

      let neighbours = node.neighbourhood('node[!group]');
      let next, parent;
      items = {
        items: items
          .map((item) => {
            if (!next && !item.hasClass('checkmark')) {
              next = item;
            }
            const accepted = this.getIndicatorType(item, 'state').length > 0 ? 'show' : '';
            const notification = this.getIndicatorType(item, 'notification').length > 0 ? 'show' : '';
            const announcement = this.getIndicatorType(item, 'announceNode').length > 0 ? 'show' : '';
            return {
              name: item.data('name'),
              uuid: item.id(),
              type: item.data('type'),
              completed: item.hasClass('checkmark'),
              accepted,
              notification,
              announcement,
            };
          })
          .sort(function (a, b) {
            if ((a.completed && b.completed) || (!a.completed && !b.completed)) {
              return 0;
            } else if (a.completed && !b.completed) {
              return -1;
            } else {
              return 1;
            }
          }),
        neighbours: neighbours
          .map(function (item) {
            if (!parent && !item.hasClass('hub_completed') && item.data('lock_state') === 'unlocked') {
              parent = item;
            }
            return {
              name: item.data('name'),
              uuid: item.id(),
              image: item.data('image'),
              completed: item.hasClass('hub_completed'),
              lock_state: item.data('lock_state'),
            };
          })
          .sort(function (a, b) {
            if ((a.completed && b.completed) || (!a.completed && !b.completed)) {
              return 0;
            } else if (a.completed && !b.completed) {
              return -1;
            } else {
              return 1;
            }
          }),
        navigation: {},
      };
      if (parent && firstHubId !== node.id()) {
        items.navigation.parent = {
          name: parent.data('name'),
          uuid: parent.id(),
          image: parent.data('image'),
        };
      }
      if (next) {
        items.navigation.next = {
          name: next.data('name'),
          uuid: next.id(),
          type: next.data('type'),
          completed: next.hasClass('checkmark'),
          image: next.data('image'),
        };
      }
    } else {
      let parent = node.neighbourhood('[!group]');
      let mask = node.data('position_mask');
      let parent_mask = parent.data('position_mask');
      let index = this.mask_values.indexOf(mask);
      let next = null;
      let count = 0;
      while (!next && count < 8) {
        if (!parent_mask) {
          break;
        }
        let n = index + 1;
        if (n === this.mask_values.length) {
          n = 0;
        }
        if (parent_mask & this.mask_values[n]) {
          const nextNode = parent.neighbourhood(`[?group][position_mask=${this.mask_values[n]}]`);
          if (!['done', 'accepted'].includes(nextNode.data('checkmark_state'))) {
            next = nextNode;
          } else {
            index = n;
          }
        } else {
          index = n;
        }
        count++;
      }
      if (!next || next.id() == node.id() || parent.hasClass('hub_completed')) {
        if (!lastHubId || lastHubId === parent.id()) {
          next = null;
        } else if (next && !next.hasClass('hub_completed') && next.data('lock_state') === 'unlocked') {
          const currentHubIndex = orderedHubIds.findIndex((hubId) => hubId === parent.id());
          const nextHubId = orderedHubIds[currentHubIndex + 1];
          next = this.graph.cy.$('#' + nextHubId);
        }
      }
      items = {
        parent: {
          name: parent.data('name'),
          uuid: parent.id(),
          image: parent.data('image'),
        },
      };
      if (next) {
        items.next = {
          name: next.data('name'),
          uuid: next.id(),
          type: next.data('type'),
          completed: next.hasClass('checkmark'),
          image: next.data('image'),
        };
      }
    }
    return items;
  }

  getGraphURL() {
    return 'learn/graph/' + this.options.circle_id + '/session/' + this.options.session_id;
  }

  supportsCommunication() {
    return true;
  }

  getURLFor(type, context, options) {
    switch (type) {
      case ContentManager.Content.Chunk:
        return `${context.getId()}/session/${options.session_id}/content`;

      case ContentManager.Content.Node:
        return 'learn/' + options.session_id + '/node/' + context.getId() + '/content';

      case ContentManager.Content.Circle:
        return 'learn/' + options.session_id + '/circle';

      case ContentManager.Content.Task:
        if (context.type == 'single_task' || context.type == 'single_group') {
          return context.getParentId() + '/session/' + options.session_id + '/report/' + context.getId();
        } else {
          return context.getId() + '/session/' + options.session_id;
        }

      case ContentManager.Content.Quiz:
        if (context.type == 'single_quiz' || context.type == 'single_written_quiz') {
          return context.getParentId() + '/session/' + options.session_id + '/report/' + context.getId();
        } else {
          return context.getId() + '/session/' + options.session_id;
        }

      case ContentManager.Content.Blog:
        if (context.type == 'single_private_blog') {
          return context.getParentId() + '/session/' + options.session_id + '/report/' + context.getId();
        } else {
          return context.getId() + '/session/' + options.session_id;
        }

      case ContentManager.Content.BlogPost:
        return 'post/' + context.getId();

      case ContentManager.Content.Legend:
        return context.getId() + '/session/' + options.session_id;

      case ContentManager.Content.Evaluation:
        return context.getId() + '/session/' + options.session_id;

      case ContentManager.Content.Correlation:
        if (context.type == 'single_correlation') {
          return context.getParentId() + '/session/' + options.session_id + '/report/' + context.getId();
        } else {
          return context.getId() + '/session/' + options.session_id;
        }
    }
  }

  markProgress(node) {
    this.remote.put('learn/' + this.options.session_id + '/node/' + node.id() + '/progress');
  }
}
